import type { CompanyResponse, LicenceResponse, ListResponse } from '@shared/schemas';
import { all, call, delay, getContext, put, race, select, take, takeLatest } from 'redux-saga/effects';

import type { EnrichmentStats } from '../../_shared/domain/stats.domain';
import { hasActiveSession } from '../../_shared/domain/stats.domain';
import { whenPageVisible } from '../../_shared/infrastructure/page-visibility';
import type { EnrichmentCandidate } from '../domain/candidates.domain';
import { type EnrichmentSession, hasRunningSession } from '../domain/sessions.domain';
import type { SessionsGateway } from '../infrastructure/sessions.gateway';
import { sessionsRequested, sessionsStopped, statsRequested, statsStopped, submitRequested } from './sessions.actions';
import {
  selectPanelCurrentOnly,
  selectPanelLicenceId,
  selectPanelListId,
  selectPanelType,
} from './sessions.selectors';
import { sessionsActions } from './sessions.slice';

/** Poll cadences (ms) while at least one session is still running (parity with the old queries). */
const SESSIONS_POLL_MS = 5_000;
const STATS_POLL_MS = 10_000;

const DEFAULT_ERROR = 'Failed to load sessions.';

/** Best-effort human message for the failure banner (no domain error union needed). */
function toMessage(e: unknown): string {
  if (e instanceof TypeError) {
    return 'Network error; please check your connection and retry.';
  }
  return e instanceof Error && e.message ? e.message : DEFAULT_ERROR;
}

// ── Sessions table: poll until no session is running ──

/**
 * WORKER: fetch the sessions table, then keep polling every 5s while a session
 * is still running (stops when idle or on error). Only the first fetch flips the
 * `loading` flag, so background refetches don't flicker the table spinner (parity
 * with React Query's isLoading vs isFetching).
 */
function* pollSessionsWorker() {
  const gateway: SessionsGateway = yield getContext('sessionsGateway');
  let first = true;
  while (true) {
    if (first) yield put(sessionsActions.loadStarted());
    try {
      const rows = (yield call(gateway.fetchSessions)) as EnrichmentSession[];
      yield put(sessionsActions.loadSucceeded(rows));
      if (!hasRunningSession(rows)) return;
    } catch (e) {
      yield put(sessionsActions.loadFailed(toMessage(e)));
      return;
    }
    first = false;
    yield delay(SESSIONS_POLL_MS);
    yield call(whenPageVisible);
  }
}

/** WORKER: run the sessions poll, cancelled by page unmount (`sessionsStopped`). */
function* sessionsPollLifecycle() {
  yield race({ poll: call(pollSessionsWorker), stop: take(sessionsStopped.type) });
}

// ── KPI stats: poll until no session is active ──

/** WORKER: fetch stats, then poll every 10s while a session is active. */
function* pollStatsWorker() {
  const gateway: SessionsGateway = yield getContext('sessionsGateway');
  while (true) {
    try {
      const stats = (yield call(gateway.fetchStats)) as EnrichmentStats;
      yield put(sessionsActions.statsLoaded(stats));
      if (!hasActiveSession(stats.recent_sessions)) return;
    } catch {
      // Non-blocking: keep the last KPI values; stop polling.
      return;
    }
    yield delay(STATS_POLL_MS);
    yield call(whenPageVisible);
  }
}

/** WORKER: run the stats poll, cancelled by page unmount (`statsStopped`). */
function* statsPollLifecycle() {
  yield race({ poll: call(pollStatsWorker), stop: take(statsStopped.type) });
}

// ── New Session panel: dropdown data ──

/** WORKER: the licence filter options (fetched when the panel opens). */
function* fetchLicencesWorker() {
  const gateway: SessionsGateway = yield getContext('sessionsGateway');
  yield put(sessionsActions.licencesLoading());
  try {
    const licences = (yield call(gateway.fetchLicences)) as LicenceResponse[];
    yield put(sessionsActions.licencesLoaded(licences));
  } catch {
    yield put(sessionsActions.licencesLoaded([]));
  }
}

/** WORKER: the type-filter options (fetched when the panel opens). */
function* fetchProfileTypesWorker() {
  const gateway: SessionsGateway = yield getContext('sessionsGateway');
  yield put(sessionsActions.profileTypesLoading());
  try {
    const types = (yield call(gateway.fetchProfileTypes)) as string[];
    yield put(sessionsActions.profileTypesLoaded(types));
  } catch {
    yield put(sessionsActions.profileTypesLoaded([]));
  }
}

/** WORKER: lists within the selected licence (empty when no licence is picked). */
function* fetchListsWorker() {
  const licenceId = (yield select(selectPanelLicenceId)) as string | null;
  if (!licenceId) {
    yield put(sessionsActions.listsLoaded([]));
    return;
  }
  const gateway: SessionsGateway = yield getContext('sessionsGateway');
  yield put(sessionsActions.listsLoading());
  try {
    const lists = (yield call(gateway.fetchLists, licenceId)) as ListResponse[];
    yield put(sessionsActions.listsLoaded(lists));
  } catch {
    yield put(sessionsActions.listsLoaded([]));
  }
}

/**
 * WORKER: the entity pool for the current scope. Fetches companies for the
 * company type, else profile candidates. Runs on open and whenever the type,
 * scope, or current-only flag changes.
 */
function* fetchEntitiesWorker() {
  const gateway: SessionsGateway = yield getContext('sessionsGateway');
  const type = (yield select(selectPanelType)) as string;
  const licenceId = (yield select(selectPanelLicenceId)) as string | null;
  const listId = (yield select(selectPanelListId)) as string | null;
  const currentOnly = (yield select(selectPanelCurrentOnly)) as boolean;
  const scope = { licenceId, listId };
  const isCompany = type === 'linkedin_company';

  yield put(sessionsActions.entitiesLoading());
  try {
    if (isCompany) {
      const companies = (yield call(gateway.fetchCompanies, scope, currentOnly)) as CompanyResponse[];
      yield put(sessionsActions.companiesLoaded(companies));
    } else {
      const candidates = (yield call(gateway.fetchCandidates, scope)) as EnrichmentCandidate[];
      yield put(sessionsActions.candidatesLoaded(candidates));
    }
  } catch {
    // Non-blocking: clear the spinner with an empty pool.
    yield put(isCompany ? sessionsActions.companiesLoaded([]) : sessionsActions.candidatesLoaded([]));
  }
}

// ── Create session ──

/** WORKER: create an enrichment session; the page reacts to succeeded/failed. */
function* createWorker(action: ReturnType<typeof submitRequested>) {
  const gateway: SessionsGateway = yield getContext('sessionsGateway');
  yield put(sessionsActions.createStarted());
  try {
    const session = (yield call(gateway.createSession, action.payload)) as EnrichmentSession;
    yield put(sessionsActions.createSucceeded(session));
  } catch (e) {
    yield put(sessionsActions.createFailed(toMessage(e)));
  }
}

/** WATCHERS. */
export function* sessionsSaga() {
  yield all([
    takeLatest(sessionsRequested.type, sessionsPollLifecycle),
    takeLatest(statsRequested.type, statsPollLifecycle),
    // Panel opens → load the licence + type-filter options.
    takeLatest(
      [sessionsActions.panelOpened.type, sessionsActions.panelReopenedFromRun.type],
      fetchLicencesWorker,
    ),
    takeLatest(
      [sessionsActions.panelOpened.type, sessionsActions.panelReopenedFromRun.type],
      fetchProfileTypesWorker,
    ),
    // Licence changes → reload the scoped lists. Re-asking arrives with one
    // already set, so its lists have to be fetched without a change event.
    takeLatest(
      [sessionsActions.panelLicenceChanged.type, sessionsActions.panelReopenedFromRun.type],
      fetchListsWorker,
    ),
    // Open or any scope/type change → reload the entity pool.
    takeLatest(
      [
        sessionsActions.panelOpened.type,
        sessionsActions.panelReopenedFromRun.type,
        sessionsActions.panelTypeChanged.type,
        sessionsActions.panelLicenceChanged.type,
        sessionsActions.panelListChanged.type,
        sessionsActions.panelCurrentOnlyToggled.type,
      ],
      fetchEntitiesWorker,
    ),
    takeLatest(submitRequested.type, createWorker),
  ]);
}
